import { defineVars } from '@stylexjs/stylex';
import { productAccents } from './palette.stylex';

/** One color and themed control-image contract for the whole UI; add semantic roles here as components migrate. */
export const colors = defineVars({
  formulaAssistantError: '#991b1b',
  productionBadgeBackground: '#28a745',
  agentVersionMenuBorder: '#e2e8f0',
  agentVersionMenuBackground: '#ffffff',

  workspaceSurface: '#ffffff',
  workspaceBorder: '#e6e7e8',
  workspaceFrameBorder: 'transparent',
  workspaceRowBorder: '#e6ecf2',
  workspaceRowBackground: productAccents.topicSubtle,
  workspaceIcon: productAccents.topicLight,
  workspaceDangerBackground: '#f2dede',

  assistantPanelBorder: '#e6e7e8',
  gaugeTrack: '#e7e7e7',
  composerPlaceholder: '#9ca3af',
  composerActionBackground: '#ffffff',
  agentDescription: '#7ea6d6',
  subduedText: '#677b7b',
  effortTrack: '#f1f5f7',
  effortSelected: '#ffffff',
  effortHover: '#dde1e3',
  guidedAccent: '#0284c7',
  guidedAccentHover: '#0369a1',
  guidedSelection: '#3b82f6',
  guidedSelectionText: '#60a5fa',
  inputBackground: '#ffffff',
  formGroupBorder: '#cccccc',
  configurationGroupBorder: '#e2e2e2',
  diffAdded: 'oklch(96.2% 0.044 156.743)',
  diffRemoved: 'oklch(93.6% 0.032 17.717)',
  historyNumber: 'oklch(92.8% 0.006 264.531)',
  historyNumberSelected: 'oklch(44.3% 0.11 240.79)',
  historyNumberText: '#ffffff',

  selectedFilterBackground: '#e2e2e2',
  readOnlyBackground: '#f5f5f5',
  readOnlyBorder: '#dde1e3',
  statusGood: '#0c684c',
  statusProgress: '#3a3a3a',
  historyWarning: 'oklch(66.6% 0.179 58.318)',
  popoverHeading: '#f1f3f4',
  separator: '#e6e7e8',
  info: '#0491c2',
  listRowAlternate: '#f1f5f7',
  readOnlyText: '#677b7b',
  subtleBorder: '#dde1e3',
  cardBorder: 'rgb(0 0 0 / 12.5%)',
  cardHeaderBackground: 'rgb(0 0 0 / 3%)',
  sidebarBorder: '#e2e2e2',
  sidebarBackground: '#f1f5f7',
  sidebarHover: '#f1f5f7',
  sidebarSelected: '#eef6fd',
  sidebarSelectedBorder: '#2a5c84',
  accent: productAccents.topicLight,
  paginationSelected: productAccents.topicLight,
  accentHover: productAccents.topicHighlight,
  accentPressed: productAccents.topicDark,
  swatchBorder: 'oklch(37.3% 0.034 259.733)',
  menuBorder: '#dde1e3',
  menuText: '#3a3a3a',
  menuTextDisabled: '#dde1e3',
  menuActionDisabled: '#dde1e3',
  notificationDanger: '#dc3545',
  dashboardTileBorder: '#e2e2e2',
  errorIconBackground: 'oklch(97.1% 0.013 17.38)',
  errorIconBorder: 'oklch(88.5% 0.062 18.334)',
  assistantActionBorder: '#a9cce6',
  assistantActionStart: '#e8f3fc',
  assistantActionEnd: '#f2f8fd',
  assistantActionText: '#0266b8',
  assistantActionIcon: '#0275d1',
  overlayPanelBackground: 'rgb(255 255 255 / 15%)',
  overlayPanelBorder: 'rgb(0 0 0 / 8%)',
  overlayPanelShadow: 'rgb(0 0 0 / 6%)',
  overlayPanelHighlight: 'transparent',
  boardTileBackground: 'transparent',
  boardTileBorder: '#e5e7eb',
  boardTileShadow: 'rgb(100 100 111 / 15%)',
  boardTileLoadingOverlay: 'rgb(255 255 255 / 45%)',
  menuHover: '#f9f9f9',
  trackBackground: '#e2e2e2',
  selectionBorder: '#e2e8f0',
  selectionBackground: '#eef6fd',
  selectionText: '#000000',
  panelBorder: '#e2e8f0',
  panelBackground: '#ffffff',
  black: '#000000',
  mutedText: '#869298',
  badgeNewBackground: '#fce7ce',
  badgeNewText: '#664d03',
  badgePositiveBackground: '#c5edd4',
  badgePositiveText: '#007960',
  badgePositiveLabelText: '#ffffff',
  badgeNeutralBackground: '#f8f6f2',
  badgeErrorBackground: '#ffede9',
  badgeNewBorder: 'rgb(240 173 78 / 40%)',
  badgePositiveBorder: 'rgb(0 121 96 / 40%)',
  badgeNeutralBorder: 'rgb(134 146 152 / 40%)',
  badgeErrorBorder: 'rgb(217 83 79 / 40%)',
  alertBackground: `color-mix(in srgb, ${productAccents.topicHighlight} 10%, transparent)`,
  formulaErrorBackground: 'oklch(88.5% 0.062 18.334)',
  formulaErrorText: 'oklch(44.4% 0.177 26.899)',
  formulaErrorBorder: 'oklch(93.6% 0.032 17.717)',
  warningBackground: '#fff3cd',
  warningText: '#3a3a3a',
  toolbarHover: '#f1f3f4',
  toolbarSelected: '#eeedee',
  toolbarSelectedPressed: '#eeedee',
  surfaceHover: '#f1f5f7',
  surfaceSelected: '#dde1e3',
  surfacePressed: '#e2e2e2',
  navigationTextActive: productAccents.topicLight,
  dangerHover: '#c9302c',
  warningHover: '#ec971f',
  accentSubtle: productAccents.topicSubtle,
  accentHighlightBackground: productAccents.topicSubtle,
  link: productAccents.topicLight,
  white: '#ffffff',
  bodyText: '#3a3a3a',
  textOnLight: '#3a3a3a',
  tooltipBackground: '#000000',
  segmentBorderInactive: '#dde1e3',
  segmentBackground: '#ffffff',
  segmentHover: '#f1f5f7',
  segmentSelected: '#d9ecff',
  segmentSelectedHover: '#c7e3ff',
  segmentBorder: '#1f5f9f',
  tabBorder: '#eeedee',
  tabHover: '#f1f5f7',
  tabLabel: productAccents.topicLight,
  tabLabelActive: 'oklch(55.1% 0.027 264.364)',
  modalBackdrop: '#0d1117',
  modalBorder: 'transparent',
  checkboxBorder: '#dde1e3',
  checkboxImage:
    "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='%23000000' d='M12.207 4.793a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0l-2-2a1 1 0 011.414-1.414L6.5 9.086l4.293-4.293a1 1 0 011.414 0z'/%3e%3c/svg%3e\")",
  checkboxDisabledImage:
    "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='%23606061' d='M12.207 4.793a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0l-2-2a1 1 0 011.414-1.414L6.5 9.086l4.293-4.293a1 1 0 011.414 0z'/%3e%3c/svg%3e\")",
  checkboxIndeterminateImage:
    "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath stroke='%23000000' stroke-width='2' stroke-linecap='round' d='M4 8h8'/%3e%3c/svg%3e\")",
  checkboxDisabledIndeterminateImage:
    "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath stroke='%23606061' stroke-width='2' stroke-linecap='round' d='M4 8h8'/%3e%3c/svg%3e\")",
  textMuted: '#b7b7b7',
  secondaryText: '#6c757d',
  mutedForeground: '#606061',
  supportingForeground: '#869298',
  controlHoverBackground: '#f1f5f9',
  contrastForeground: '#000000',
  secondaryBackground: '#f7f7f8',
  contrastSelectedBackground: 'rgb(0 0 0 / 10%)',
  secondaryForeground: 'oklch(55.4% 0.046 257.417)',
  surfaceSubtle: '#f1f5f7',
  tableRowAlternate: '#f9f9f9',
  divider: '#e2e8f0',
  textSubtle: '#606061',
  iconMuted: '#dde1e3',
  warning: '#f0ad4e',
  success: '#72c02c',
  icon: productAccents.topicIcon,
  iconForeground: productAccents.topicIcon,
  background: '#ffffff',
  // Let standalone light fields inherit their containing surface.
  fieldBackground: 'initial',
  backgroundDisabled: '#f1f5f7',
  border: '#dde1e3',
  borderInvalid: '#d9534f',
  borderDisabled: '#dde1e3',
  text: '#3a3a3a',
  textDisabled: '#b7b7b7',
  placeholder: 'oklch(70.7% 0.022 261.325)',
  borderHover: productAccents.topicLight,
  borderFocus: productAccents.topicLight,
  borderActive: productAccents.topicLight,
});
